import React from 'react';
import {
  GroupsOutlined,
  NotesOutlined,
  PublicOutlined,
  TrackChangesOutlined,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import { Desc, Title } from './index.styles';
import { StyledTextField } from './productInformation';
import GoalsBasic from './forms/goals-basic';
import Assertions from './forms/assertions';
import ToggleButtonLCA from './ToggleButton';
import { Trans, useTranslation } from 'react-i18next';
import SectionCard from './SectionCard';
import { useProductFieldError } from './validation';
import { ComplianceField } from 'src/Lca/compliance';

interface Props {
  values: {
    regulatory_frameworks: string;
    goals: {
      intended_applications: string;
      study_reasons: string;
      target_audience: string;
      comissioner_name: string;
      external_experts_involved: boolean;
      external_experts_description: string;
      results_disclosed: boolean;
      assertions_disclosed: boolean;
      results_confidential: boolean;
      assertions_confidential: boolean;
      disclosure_description: string;
      comment: string;
    };
  };
  setValues: Function;
}

export default function Goals({ setValues, values }: Props) {
  const { t } = useTranslation('lca', { keyPrefix: 'goals' });
  const externalExpertsError = useProductFieldError(
    'external_experts_involved',
    values.goals.external_experts_involved
  );
  const externalExpertsDescriptionError = useProductFieldError(
    'external_experts_description',
    values.goals.external_experts_description
  );
  const commentError = useProductFieldError('comment', values.goals.comment);
  const handle_external_experts_involved = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      goals: {
        ...values.goals,
        external_experts_involved: e.target.value === 'true',
      },
    });
  };
  const handle_external_experts_description = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      goals: {
        ...values.goals,
        external_experts_description: e.target.value,
      },
    });
  };

  const handle_comment = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      goals: {
        ...values.goals,
        comment: e.target.value,
      },
    });
  };

  const options = [
    { name: t('yes'), value: true },
    { name: t('no'), value: false },
  ];
  const sections = [
    {
      key: 'basicInformation',
      title: t('basicInformation'),
      subtitle: t('sectionDescriptions.basicInformation'),
      icon: <TrackChangesOutlined sx={{ color: '#7e4308' }} />,
      content: <GoalsBasic values={values} setValues={setValues} />,
    },
    {
      key: 'externalExperts',
      title: t('externalExperts'),
      subtitle: t('sectionDescriptions.externalExperts'),
      icon: <GroupsOutlined sx={{ color: '#7e4308' }} />,
      content: (
        <Box>
          <ComplianceField
            excelRow={110}
            regulatoryFrameworks={values.regulatory_frameworks}
          >
            <ToggleButtonLCA
              options={options}
              setState={handle_external_experts_involved}
              state={values.goals.external_experts_involved}
              errorText={externalExpertsError}
            />
          </ComplianceField>
          {values.goals.external_experts_involved && (
            <ComplianceField
              excelRow={111}
              regulatoryFrameworks={values.regulatory_frameworks}
            >
              <StyledTextField
                style={{ marginTop: 24 }}
                fullWidth
                label={t('nameThem')}
                name="external_experts_description"
                value={values.goals.external_experts_description}
                onChange={handle_external_experts_description}
                multiline
                rows={4}
                error={
                  !!externalExpertsDescriptionError || !!externalExpertsError
                }
                helperText={
                  externalExpertsDescriptionError || externalExpertsError
                }
              />
            </ComplianceField>
          )}
        </Box>
      ),
    },
    {
      key: 'disclosure',
      title: t('disclosure'),
      subtitle: t('sectionDescriptions.disclosure'),
      icon: <PublicOutlined sx={{ color: '#7e4308' }} />,
      content: <Assertions values={values} setValues={setValues} />,
    },
    {
      key: 'additionalInformation',
      title: t('additionalInformation'),
      subtitle: t('sectionDescriptions.additionalInformation'),
      icon: <NotesOutlined sx={{ color: '#7e4308' }} />,
      content: (
        <StyledTextField
          fullWidth
          label={t('additionalInformationQuestion')}
          name="comment"
          value={values.goals.comment}
          onChange={handle_comment}
          multiline
          rows={3}
          error={!!commentError}
          helperText={commentError}
        />
      ),
    },
  ];
  const leftColumnSections = [sections[0], sections[2]];
  const rightColumnSections = [sections[1], sections[3]];

  return (
    <div>
      <Title>{t('title')}</Title>
      <Desc>
        <Trans t={t} i18nKey="desc" />
      </Desc>
      <Box
        sx={{
          display: { xs: 'flex', xl: 'none' },
          flexDirection: 'column',
          gap: '24px',
          mt: '40px',
        }}
      >
        {sections.map((section) => (
          <SectionCard
            key={section.key}
            icon={section.icon}
            title={section.title}
            subtitle={section.subtitle}
          >
            {section.content}
          </SectionCard>
        ))}
      </Box>

      <Box
        sx={{
          display: { xs: 'none', xl: 'grid' },
          gridTemplateColumns: '1fr 1fr',
          gap: '24px',
          mt: '40px',
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          {leftColumnSections.map((section) => (
            <SectionCard
              key={section.key}
              icon={section.icon}
              title={section.title}
              subtitle={section.subtitle}
            >
              {section.content}
            </SectionCard>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          {rightColumnSections.map((section) => (
            <SectionCard
              key={section.key}
              icon={section.icon}
              title={section.title}
              subtitle={section.subtitle}
            >
              {section.content}
            </SectionCard>
          ))}
        </Box>
      </Box>
    </div>
  );
}
